import React, { useEffect, useMemo, useState } from "react";
import { GripVertical, ArrowUp, ArrowDown, Trash2, Plus, Save, AlertTriangle, Info, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const ROUTABLE = ["HOME", "STORE"];

export function RoutingDialog({ gid, groupName, canEdit, onClose }) {
  const [data, setData] = useState(null);
  const [lists, setLists] = useState({ HOME: [], STORE: [] });
  const [drag, setDrag] = useState({ bucket: null, index: null });
  const [preview, setPreview] = useState(null);

  const load = () => api.get(`/customer-groups/${gid}/routing`)
    .then((r) => {
      setData(r.data);
      setLists({ HOME: r.data.buckets.HOME.warehouses.map(clone), STORE: r.data.buckets.STORE.warehouses.map(clone) });
    })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gid]);

  const clone = (w) => ({ warehouse_id: w.warehouse_id, warehouse_name: w.warehouse_name, available: w.available, is_active: w.is_active });
  const availMap = useMemo(() => Object.fromEntries((data?.eligible_warehouses || []).map((w) => [w.id, w])), [data]);

  const setBucket = (b, next) => setLists((s) => ({ ...s, [b]: next }));
  const move = (b, i, dir) => {
    const arr = [...lists[b]]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setBucket(b, arr);
  };
  const toggle = (b, i, v) => {
    const arr = [...lists[b]];
    if (!v && arr.filter((x) => x.is_active).length === 1 && (data?.buckets?.[b]?.allowed)) {
      if (!window.confirm("Ini routing aktif terakhir untuk bucket ini. Nonaktifkan tetap? Menyimpan tanpa routing aktif akan ditolak.")) return;
    }
    arr[i] = { ...arr[i], is_active: v }; setBucket(b, arr);
  };
  const removeWh = (b, i) => {
    const arr = [...lists[b]];
    if (arr[i].is_active && arr.filter((x) => x.is_active).length === 1 && data?.buckets?.[b]?.allowed) {
      if (!window.confirm("Ini routing aktif terakhir. Hapus tetap? Bucket akan menjadi 'Perlu Pengaturan'.")) return;
    }
    arr.splice(i, 1); setBucket(b, arr);
  };
  const addWh = (b, wid) => {
    if (lists[b].some((x) => x.warehouse_id === wid)) return toast.error("Gudang sudah ada di routing ini");
    const w = availMap[wid];
    setBucket(b, [...lists[b], { warehouse_id: wid, warehouse_name: w?.name || "", available: w?.available || 0, is_active: true }]);
  };
  const onDrop = (b, i) => {
    if (drag.bucket !== b || drag.index === null || drag.index === i) return setDrag({ bucket: null, index: null });
    const arr = [...lists[b]]; const [m] = arr.splice(drag.index, 1); arr.splice(i, 0, m);
    setBucket(b, arr); setDrag({ bucket: null, index: null });
  };

  const totalActive = (b) => lists[b].filter((x) => x.is_active).reduce((s, x) => s + (x.available || 0), 0);

  const openPreview = (b) => {
    if (!lists[b].some((x) => x.is_active) && data?.buckets?.[b]?.allowed) return toast.error(`Minimal satu routing aktif untuk bucket ${b}`);
    const before = (data?.buckets?.[b]?.warehouses || []).map((w) => w.warehouse_name);
    const after = lists[b].map((w) => w.warehouse_name + (w.is_active ? "" : " (nonaktif)"));
    setPreview({ bucket: b, before, after });
  };
  const save = async () => {
    const b = preview.bucket;
    const payload = { warehouses: lists[b].map((w, i) => ({ warehouse_id: w.warehouse_id, priority: i + 1, is_active: w.is_active })) };
    try {
      await api.put(`/customer-groups/${gid}/routing/${b}`, payload);
      toast.success(`Routing ${b} disimpan (berlaku untuk transaksi baru)`); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const available_to_add = (b) => (data?.eligible_warehouses || []).filter((w) => !lists[b].some((x) => x.warehouse_id === w.id));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="routing-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Routing Keep &amp; Sales — {groupName}</DialogTitle>
        </DialogHeader>
        {!data ? <div className="py-10 text-center text-wmuted">Memuat...</div> : (
          <>
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-900 text-xs">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
              <span>Perubahan routing hanya berlaku untuk <b>Keep &amp; Sales baru</b>, tidak memindahkan stok, tidak mengubah On Hand, dan tidak mengubah transaksi lama. Semua perubahan tercatat di Audit Log.</span>
            </div>
            <Tabs defaultValue="HOME">
              <TabsList data-testid="routing-tabs">
                <TabsTrigger value="HOME" data-testid="routing-tab-HOME">HOME</TabsTrigger>
                <TabsTrigger value="STORE" data-testid="routing-tab-STORE">STORE</TabsTrigger>
                <TabsTrigger value="PRE_ORDER" data-testid="routing-tab-PRE_ORDER">PRE_ORDER</TabsTrigger>
              </TabsList>

              {ROUTABLE.map((b) => (
                <TabsContent key={b} value={b}>
                  {!data.buckets[b].allowed ? (
                    <div className="py-8 text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid={`routing-disabled-${b}`}>
                      <AlertTriangle className="w-5 h-5 mx-auto mb-1.5" />
                      Bucket <b>{b}</b> belum diizinkan untuk group ini. Aktifkan pada pengaturan Umum group (Bucket yang diizinkan).
                    </div>
                  ) : (
                    <div data-testid={`routing-panel-${b}`}>
                      <div className="space-y-2">
                        {lists[b].map((w, i) => (
                          <div key={w.warehouse_id}
                            draggable={canEdit}
                            onDragStart={() => setDrag({ bucket: b, index: i })}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => onDrop(b, i)}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 bg-white ${w.is_active ? "border-taupe/30" : "border-taupe/15 opacity-60"}`}
                            data-testid={`routing-row-${b}-${w.warehouse_id}`}>
                            {canEdit && <GripVertical className="w-4 h-4 text-taupe cursor-grab shrink-0" />}
                            <span className="w-6 h-6 rounded-full bg-cocoa/10 text-cocoa text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-cocoa truncate">{w.warehouse_name}</div>
                              <div className="text-xs text-wmuted">Available <b className="text-status-passed">{num(w.available)}</b></div>
                            </div>
                            {canEdit && <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(b, i, -1)} data-testid={`routing-up-${b}-${w.warehouse_id}`}><ArrowUp className="w-4 h-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === lists[b].length - 1} onClick={() => move(b, i, 1)} data-testid={`routing-down-${b}-${w.warehouse_id}`}><ArrowDown className="w-4 h-4" /></Button>
                              <div className="flex items-center gap-1"><Switch checked={w.is_active} onCheckedChange={(v) => toggle(b, i, v)} data-testid={`routing-active-${b}-${w.warehouse_id}`} /></div>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-status-reject" onClick={() => removeWh(b, i)} data-testid={`routing-remove-${b}-${w.warehouse_id}`}><Trash2 className="w-4 h-4" /></Button>
                            </>}
                            {!canEdit && <Badge variant="outline" className={w.is_active ? "text-status-passed border-green-300" : "text-wmuted"}>{w.is_active ? "Aktif" : "Nonaktif"}</Badge>}
                          </div>
                        ))}
                        {!lists[b].length && <div className="py-6 text-center text-wmuted text-sm"><Warehouse className="w-6 h-6 mx-auto mb-1.5 opacity-50" />Belum ada gudang. Tambahkan minimal satu gudang aktif.</div>}
                      </div>

                      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                        <div className="text-sm text-cocoa">Total {b} Available: <b className="text-status-passed" data-testid={`routing-total-${b}`}>{num(totalActive(b))}</b></div>
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            <Select value="" onValueChange={(v) => addWh(b, v)}>
                              <SelectTrigger className="w-56 h-9 bg-white" data-testid={`routing-add-select-${b}`}><SelectValue placeholder="+ Tambah gudang..." /></SelectTrigger>
                              <SelectContent>
                                {available_to_add(b).length === 0 && <div className="px-3 py-2 text-sm text-wmuted">Semua gudang sellable sudah dipakai</div>}
                                {available_to_add(b).map((w) => <SelectItem key={w.id} value={w.id}>{w.name} · avail {num(w.available)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button onClick={() => openPreview(b)} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-9" data-testid={`routing-save-${b}`}><Save className="w-4 h-4 mr-1.5" />Simpan {b}</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              ))}

              <TabsContent value="PRE_ORDER">
                <div className="py-8 text-center text-blue-900 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="routing-panel-PRE_ORDER">
                  <Info className="w-5 h-5 mx-auto mb-1.5 text-blue-600" />
                  PRE_ORDER tidak memakai routing gudang. Kelola kuota Pre-Order di menu <b>Pre-Order Quota</b>.
                  <div className="mt-1 text-xs">Diizinkan untuk group ini: <b>{data.buckets.PRE_ORDER.allowed ? "Ya" : "Tidak"}</b></div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Tutup</Button></DialogFooter>
      </DialogContent>

      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent className="max-w-md" data-testid="routing-preview-dialog">
            <DialogHeader><DialogTitle className="font-heading text-cocoa">Preview Perubahan Routing {preview.bucket}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div><div className="text-xs text-wmuted mb-1">Sebelum</div><div className="rounded border border-taupe/20 px-3 py-2 bg-taupe/5">{preview.before.length ? preview.before.join("  →  ") : "(kosong)"}</div></div>
              <div><div className="text-xs text-wmuted mb-1">Sesudah</div><div className="rounded border border-status-passed/30 px-3 py-2 bg-status-passed/5 font-medium text-cocoa">{preview.after.length ? preview.after.join("  →  ") : "(kosong)"}</div></div>
              <p className="text-xs text-wmuted">Perubahan berlaku untuk transaksi baru. Stok fisik & transaksi lama tidak berubah.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} className="border-taupe text-cocoa">Batal</Button>
              <Button onClick={save} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="routing-preview-confirm">Simpan Perubahan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
